<template>
  <div :class="MARKDOWN_TYPOGRAPHY" data-testid="wiki-text-block">
    <template v-if="variant === 'lockedSnippet'">
      <span v-html="lockedPreviewSnippet" /><span :class="LOCKED" inert>{{ lockedBlurredSnippet }}</span>
    </template>
    <p v-else-if="variant === 'lockedPlaintext'" v-html="plaintextContent" :class="LOCKED" inert />
    <div v-else-if="variant === 'lockedMarkdown'" v-html="markdownContent" :class="LOCKED" inert />
    <div v-else-if="variant === 'markdown'" v-html="markdownContent" />
    <p v-else-if="variant === 'plaintext'" v-html="plaintextContent" />
    <p v-else-if="variant === 'tokens'">
      <span v-for="(token, index) of textTokens" :key="index" class="inline">
        <span v-if="typeof token === 'string'" v-html="renderInlineMarkdown(token)" />
        <TooltipProvider v-else-if="token.type === 'link' && lockedLinks" :delay-duration="0">
          <Tooltip>
            <TooltipTrigger as-child>
              <button type="button" class="text-blue-600 underline" aria-disabled="true" @click.stop>{{ token.inlineText }}</button>
            </TooltipTrigger>
            <TooltipContent>{{ LOCKED_LINK_MESSAGE }}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <a v-else-if="token.type === 'link'" :href="token.href" target="_blank" class="text-blue-600 underline" @click.stop
          >{{ token.inlineText }}<OpensInNewTabIcon external-link class="ml-0.5"
        /></a>
        <TooltipProvider v-else-if="token.type === 'strategy'" :delay-duration="50">
          <Tooltip>
            <TooltipTrigger as-child>
              <a
                :href="strategyHref(token)"
                target="_blank"
                class="inline-block bg-yellow-200 px-1 my-px"
                :aria-label="strategyLinkLabel(token)"
                @click.stop
              ><span v-html="renderInlineMarkdown(token.inlineText)" /> <span class="material-icons-round select-none text-xs" aria-hidden="true">open_in_new</span></a>
            </TooltipTrigger>
            <TooltipContent :side="strategyPopoverSide" :collision-padding="POPOVER_SCREEN_GAP" class="max-w-[min(20rem,calc(100vw-1rem))]">
              <p class="font-bold mb-1">{{ token.title }}</p>
              <IKImageS3Wrapper
                v-if="token.image_url"
                class="max-h-[120px] mb-1"
                :aws-url="token.image_url"
                :alt="token.title"
                :transformation="[{ w: 150 }]"
              />
              <p>{{ token.description }}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <ResourceReferencePopover
          v-else-if="token.type === 'resource'"
          :resource="token.resource"
          :label="token.inlineText"
          :side="popoverPlacement"
        />
      </span>
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useMediaQuery } from '@vueuse/core'
import { tokenizeWikiText, wikiToPlainText } from '@/util/tokenize_wiki_text'
import { renderMarkdown, renderInlineMarkdown, MARKDOWN_TYPOGRAPHY } from './wiki_text_markdown'
import { toolkitUrls } from '@/util/links.js'
import { LOCKED_LINK_MESSAGE } from '@/util/access_visibility'
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/tooltip'
import OpensInNewTabIcon from '@/components/OpensInNewTabIcon.vue'
import IKImageS3Wrapper from '@/imagekit/IKImageS3Wrapper.vue'
import ResourceReferencePopover from '@/resources/ResourceReferencePopover.vue'
import type { Resource } from '@/resources/resource_types'
import type { PopoverSide, WikiTextStrategy } from './wiki_text_types'

type StrategyToken = {
  type: 'strategy'
  inlineText: string
  title: string
  image_url: string
  description: string
  slug: string
  linkQueryParams: Record<string, string>
}
type ResourceToken = { type: 'resource', inlineText: string, resource: Resource }
type LinkToken = { type: 'link', inlineText: string, href: string }
type WikiTextToken = string | StrategyToken | ResourceToken | LinkToken
type TokenizerResources = Parameters<typeof tokenizeWikiText>[2]

const LOCKED = 'select-none text-[transparent] [text-shadow:0_0_14px_black] [&_img]:blur-[5px]'

const props = withDefaults(defineProps<{
  text?: string
  strategies?: Record<string, WikiTextStrategy>
  resources?: Record<string, Resource>
  plaintext?: boolean
  popoverPlacement?: PopoverSide
  markdownOnly?: boolean
  lockedLinks?: boolean
  locked?: boolean
  lockedFull?: boolean
}>(), {
  text: '',
  strategies: () => ({}),
  resources: () => ({}),
  popoverPlacement: 'right',
})

const POPOVER_SCREEN_GAP = 8

// A max-w-xs popover beside an inline link can't fit on a phone either side, and reka only flips
// left/right, so phones open it below where it can shift sideways onto the screen.
const isPhoneWidth = useMediaQuery('(max-width: 639px)')
const strategyPopoverSide = computed(() => (isPhoneWidth.value ? 'bottom' : props.popoverPlacement))

const variant = computed(() => {
  if (props.locked) {
    if (!props.lockedFull && (!props.markdownOnly || props.plaintext)) return 'lockedSnippet'
    if (props.lockedFull && !props.markdownOnly) return 'lockedPlaintext'
    if (props.markdownOnly && !props.plaintext) return 'lockedMarkdown'
    return 'none'
  }
  if (props.markdownOnly) return 'markdown'
  return props.plaintext ? 'plaintext' : 'tokens'
})

const textTokens = computed(() =>
  (tokenizeWikiText(props.text, props.strategies, props.resources as TokenizerResources) as WikiTextToken[])
)

const plainWords = computed(() => wikiToPlainText(props.text).split(' '))
const lockedPreviewSnippet = computed(() => plainWords.value.slice(0, plainWords.value.length / 2).join(' ') + '...')
const lockedBlurredSnippet = computed(() => plainWords.value.slice(plainWords.value.length / 2).join(' '))
const plaintextContent = computed(() => renderInlineMarkdown(wikiToPlainText(props.text)))
const markdownContent = computed(() => renderMarkdown(props.text))

function inlineMarkdownAsPlainText(text: string): string {
  const rendered = new DOMParser().parseFromString(renderInlineMarkdown(text), 'text/html').body
  rendered.querySelectorAll('br').forEach((lineBreak) => lineBreak.replaceWith(' '))
  return rendered.textContent ?? ''
}

const strategyLinkLabel = (token: StrategyToken) =>
  `${inlineMarkdownAsPlainText(token.inlineText)}: Open ${token.title} strategy (opens in a new tab)`

const strategyHref = (token: StrategyToken) => {
  const query = new URLSearchParams(token.linkQueryParams).toString()
  const url = toolkitUrls.strategyPage(token.slug)
  return query ? `${url}?${query}` : url
}
</script>
